import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { HelpCircle, Play, ChevronRight, ChevronLeft, X } from "lucide-react";

// Tour steps for different pages
export const DASHBOARD_TOUR_STEPS = [
  {
    target: '[data-testid="upload-file-btn"]',
    content: "📄 Upload your documents here! We support Word, Excel, PDF, Text, and CSV files. Just click and select your file.",
    title: "Upload Documents",
  },
  {
    target: '[data-testid="text-input-btn"]',
    content: "✍️ Or you can type or paste your content directly! Perfect for song lyrics or quick notes.",
    title: "Enter Text Directly",
  },
  {
    target: '[data-testid="new-project-btn"]',
    content: "📁 Create an empty project to build your slides manually, one by one.",
    title: "New Project",
  },
  {
    target: '[data-testid="tab-setlists"]',
    content: "🎵 Setlists let you combine multiple songs for your worship service. Create one and play through all songs seamlessly!",
    title: "Setlists for Worship",
  },
];

export const EDITOR_TOUR_STEPS = [
  {
    target: '[data-testid="add-slide-btn"]',
    content: "➕ Add new slides here. Choose from templates like Verse, Chorus, Scripture, or create your own!",
    title: "Add Slides",
  },
  {
    target: '[data-testid="generate-btn"]',
    content: "✨ Let AI create slides for you! Just paste your text and we'll format it beautifully.",
    title: "AI Generation",
  },
  {
    target: '[data-testid="slide-preview"]',
    content: "👁️ This is your slide preview. See exactly how it will look during presentation!",
    title: "Live Preview",
  },
  {
    target: '[data-testid="change-wallpaper-btn"]',
    content: "🖼️ Change your slide background! Choose from presets, upload your own image, or generate one with AI.",
    title: "Backgrounds",
  },
  {
    target: '[data-testid="present-btn"]',
    content: "🎬 Ready to present? Click here for fullscreen presentation mode!",
    title: "Present",
  },
];

// Spotlight overlay component
const Spotlight = ({ targetRect, onClose }) => {
  if (!targetRect) return null;

  const padding = 8;
  const spotlightStyle = {
    position: 'fixed',
    top: targetRect.top - padding,
    left: targetRect.left - padding,
    width: targetRect.width + padding * 2,
    height: targetRect.height + padding * 2,
    borderRadius: '12px',
    boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.75)',
    pointerEvents: 'none',
    zIndex: 9998,
  };

  return createPortal(
    <div style={spotlightStyle} />,
    document.body
  );
};

// Tooltip component
const TourTooltip = ({ step, currentStep, totalSteps, onNext, onPrev, onClose, targetRect }) => {
  if (!targetRect) return null;

  // Position tooltip below or above target
  const tooltipWidth = 320;
  const tooltipHeight = 200;
  const padding = 16;
  
  let top = targetRect.bottom + padding;
  let left = targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2);
  
  // Adjust if tooltip goes off screen
  if (top + tooltipHeight > window.innerHeight) {
    top = targetRect.top - tooltipHeight - padding;
  }
  if (left < padding) left = padding;
  if (left + tooltipWidth > window.innerWidth - padding) {
    left = window.innerWidth - tooltipWidth - padding;
  }

  return createPortal(
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      style={{
        position: 'fixed',
        top,
        left,
        width: tooltipWidth,
        zIndex: 10000,
      }}
      className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl p-5 border border-slate-200 dark:border-slate-700"
    >
      <button 
        onClick={onClose}
        className="absolute top-2 right-2 p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800"
      >
        <X className="w-4 h-4 text-muted-foreground" />
      </button>
      
      {step.title && (
        <h3 className="font-bold text-lg mb-2 text-amber-600 pr-6">{step.title}</h3>
      )}
      <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
        {step.content}
      </p>
      
      <div className="flex justify-between items-center mt-4 pt-3 border-t border-slate-200 dark:border-slate-700">
        <span className="text-xs text-muted-foreground">
          Step {currentStep + 1} of {totalSteps}
        </span>
        <div className="flex gap-2">
          {currentStep > 0 && (
            <Button onClick={onPrev} variant="outline" size="sm">
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
          )}
          <Button onClick={onNext} size="sm" className="bg-amber-500 hover:bg-amber-600 text-black gap-1">
            {currentStep === totalSteps - 1 ? "Finish" : "Next"}
            {currentStep < totalSteps - 1 && <ChevronRight className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </motion.div>,
    document.body
  );
};

export const AppTour = ({ steps, run, onFinish }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);

  const updateTargetRect = useCallback(() => {
    if (!run || !steps[currentStep]) return;
    
    const target = document.querySelector(steps[currentStep].target);
    if (target) {
      const rect = target.getBoundingClientRect();
      setTargetRect(rect);
    } else {
      setTargetRect(null);
    }
  }, [run, currentStep, steps]);

  useEffect(() => {
    if (run) {
      setCurrentStep(0);
      updateTargetRect();
    }
  }, [run]);

  useEffect(() => {
    updateTargetRect();
    window.addEventListener('resize', updateTargetRect);
    window.addEventListener('scroll', updateTargetRect);
    return () => {
      window.removeEventListener('resize', updateTargetRect);
      window.removeEventListener('scroll', updateTargetRect);
    };
  }, [updateTargetRect]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      onFinish?.();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleClose = () => {
    onFinish?.();
  };

  if (!run || !steps.length) return null;

  return (
    <AnimatePresence>
      <Spotlight targetRect={targetRect} onClose={handleClose} />
      <TourTooltip
        step={steps[currentStep]}
        currentStep={currentStep}
        totalSteps={steps.length}
        onNext={handleNext}
        onPrev={handlePrev}
        onClose={handleClose}
        targetRect={targetRect}
      />
    </AnimatePresence>
  );
};

// Welcome dialog for first-time users
export const WelcomeTour = ({ onStartTour, onSkip }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const hasSeenTour = localStorage.getItem('slidecon_tour_seen');
    if (!hasSeenTour) {
      setOpen(true);
    }
  }, []);

  const handleStartTour = () => {
    setOpen(false);
    onStartTour?.();
  };

  const handleSkip = () => {
    localStorage.setItem('slidecon_tour_seen', 'true');
    setOpen(false);
    onSkip?.();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <HelpCircle className="w-8 h-8 text-black" />
          </div>
          <DialogTitle className="text-center text-2xl">Welcome to SlideCon! 🎉</DialogTitle>
          <DialogDescription className="text-center text-base">
            Would you like a quick tour to learn how to use the app? It only takes a minute!
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-3 py-4">
          <div className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
            <span className="text-2xl">📄</span>
            <div>
              <p className="font-medium text-sm">Upload & Convert</p>
              <p className="text-xs text-muted-foreground">Turn documents into slides instantly</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
            <span className="text-2xl">🎵</span>
            <div>
              <p className="font-medium text-sm">Song Mode</p>
              <p className="text-xs text-muted-foreground">Perfect for church worship lyrics</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
            <span className="text-2xl">🖥️</span>
            <div>
              <p className="font-medium text-sm">Present Anywhere</p>
              <p className="text-xs text-muted-foreground">Works with projectors, OBS & streaming</p>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={handleSkip} className="w-full sm:w-auto">
            Skip for now
          </Button>
          <Button onClick={handleStartTour} className="w-full sm:w-auto bg-amber-500 hover:bg-amber-600 text-black gap-2">
            <Play className="w-4 h-4" />
            Start Tour
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Help button to restart tour
export const TourHelpButton = ({ onClick }) => (
  <Button
    variant="outline"
    size="sm"
    onClick={onClick}
    className="gap-2"
    data-testid="start-tour-btn"
  >
    <HelpCircle className="w-4 h-4" />
    App Tour
  </Button>
);

export default AppTour;
